/**
 * Users — Kullanıcı Yönetimi (Faz 52.5 Sadeleştirilmiş)
 *
 * Minimum ekran: Ad Soyad / E-posta / Rol / Sil
 * Davet formu: Yalnızca ad + e-posta
 * Rol ve şantiye tanımları `/roller-izinler` sayfasında yönetilir.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useConfirm } from "@/components/ConfirmDialog";
import { SortableTh, useSortableRows } from "@/components/SortableTh";
import {
  Plus, TrashSimple, Copy, EnvelopeSimple, X, PaperPlaneTilt,
  WarningCircle, CheckCircle, Users as UsersIcon, Crown, Info, ShareNetwork,
} from "@phosphor-icons/react";

export default function Users() {
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [responsibilities, setResponsibilities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [inviting, setInviting] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, i, r] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/invites`).catch(() => ({ data: [] })),
        axios.get(`${API}/responsibilities`),
      ]);
      setUsers(u.data || []);
      setInvites(i.data || []);
      setResponsibilities(r.data?.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const usersSort = useSortableRows(users, {
    defaultKey: "name",
    getters: {
      name: (r) => r?.name,
      email: (r) => r?.email,
      role: (r) => (r?.role || "").toLowerCase(),
    },
  });

  const respMap = useMemo(() => {
    const map = {};
    for (const r of responsibilities) map[r.id] = r;
    return map;
  }, [responsibilities]);

  const invite = async () => {
    const email = form.email.trim().toLowerCase();
    const name = form.name.trim();
    if (!name || !email) {
      toast.error("Ad ve e-posta zorunludur");
      return;
    }
    setInviting(true);
    try {
      const r = await axios.post(`${API}/auth/invite`, { name, email });
      const data = r.data || {};
      if (data.invite_link) setCreatedLink(data.invite_link);
      toast.success(data.email_sent ? "Davet e-postası gönderildi" : "Davet oluşturuldu — linki paylaşın");
      setForm({ name: "", email: "" });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setInviting(false); }
  };

  const deleteUser = async (u) => {
    if ((u.role || "").toLowerCase() === "yonetici") {
      const ok = await askConfirm({
        title: "Yöneticiyi sil",
        description: `"${u.name}" yönetici rolünde. Silmek istediğinizden emin misiniz?`,
        confirmLabel: "Sil",
        destructive: true,
      });
      if (!ok) return;
    } else {
      const ok = await askConfirm({
        title: "Kullanıcıyı sil",
        description: `"${u.name}" kullanıcısı kalıcı olarak silinecek.`,
        confirmLabel: "Sil",
        destructive: true,
      });
      if (!ok) return;
    }
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteInvite = async (inv) => {
    const ok = await askConfirm({
      title: "Daveti iptal et",
      description: `"${inv.email}" davetini iptal etmek istediğinizden emin misiniz?`,
      confirmLabel: "İptal Et",
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/invites/${inv.id}`);
      toast.success("Davet iptal edildi");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link kopyalandı");
  };

  const renderResponsibilityBadges = (u) => {
    const admin = (u.role || "").toLowerCase() === "yonetici";
    const assigned = (u.responsibilities || [])
      .map((rid) => respMap[rid])
      .filter(Boolean);
    if (!admin && assigned.length === 0) {
      return <span className="text-xs text-slate-400 italic">rol atanmamış</span>;
    }
    return (
      <div className="flex flex-wrap items-center gap-1">
        {admin && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
            <Crown size={12} weight="fill" /> Yönetici
          </span>
        )}
        {assigned.map((r) => (
          <span
            key={r.id}
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200 text-[11px] font-medium"
            title={r.description}
          >
            {r.name}
          </span>
        ))}
      </div>
    );
  };

  return (
    <Shell>
      <div className="max-w-[1200px] mx-auto px-4 py-6 space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <UsersIcon size={26} weight="duotone" className="text-[#1B4776]" />
              Kullanıcılar
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Kullanıcıları davet edin veya silin. Rol ve şantiye yetkileri{" "}
              <a href="/roller-izinler" className="text-[#1B4776] hover:underline font-medium">
                Roller & İzinler
              </a>{" "}
              sayfasından yönetilir.
            </p>
          </div>
          <Button
            onClick={() => { setCreatedLink(null); setInviteOpen(true); }}
            className="bg-[#1B4776] hover:bg-[#163a63] text-white"
            data-testid="btn-invite-user"
          >
            <Plus size={16} className="mr-1.5" />
            Yeni Kullanıcı Davet Et
          </Button>
        </div>

        {/* Info */}
        <Card className="p-4 border-sky-200 bg-sky-50/60">
          <div className="flex items-start gap-3">
            <Info size={18} weight="duotone" className="text-sky-600 shrink-0 mt-0.5" />
            <div className="text-sm text-sky-900 leading-relaxed">
              Davet edilen kullanıcı başlangıçta hiçbir modüle erişemez. Davet
              kabul edildikten sonra <b>Roller & İzinler</b> sayfasından
              sorumluluk atayın.
            </div>
          </div>
        </Card>

        {/* Users Table */}
        <Card className="overflow-hidden border-slate-200">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <SortableTh k="name" sort={usersSort.sort} onSort={usersSort.toggleSort} className="text-left px-4 py-3 font-semibold text-slate-700">
                  Ad Soyad
                </SortableTh>
                <SortableTh k="email" sort={usersSort.sort} onSort={usersSort.toggleSort} className="text-left px-4 py-3 font-semibold text-slate-700">
                  E-posta
                </SortableTh>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Rol</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700 w-24">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-12 text-slate-400">Yükleniyor…</td></tr>
              ) : usersSort.sortedRows.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-12 text-slate-400">
                  Henüz kullanıcı yok.
                </td></tr>
              ) : usersSort.sortedRows.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">{renderResponsibilityBadges(u)}</td>
                  <td className="text-right px-4 py-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      disabled={u.id === user?.id}
                      onClick={() => deleteUser(u)}
                      title={u.id === user?.id ? "Kendinizi silemezsiniz" : "Sil"}
                      data-testid={`btn-del-user-${u.email}`}
                    >
                      <TrashSimple size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Invites Table */}
        {invites.length > 0 && (
          <>
            <h2 className="text-lg font-semibold text-slate-900 mt-6 flex items-center gap-2">
              <EnvelopeSimple size={20} weight="duotone" className="text-slate-500" />
              Bekleyen Davetler
            </h2>
            <Card className="overflow-hidden border-slate-200">
              <table className="w-full text-sm" data-testid="invites-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Ad Soyad</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">E-posta</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-700">Durum</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700 w-32">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => {
                    const emailSent = inv.email_sent === true;
                    const emailFailed = inv.email_sent === false;
                    return (
                      <tr key={inv.id} className="border-b border-slate-100" data-testid={`invite-row-${inv.email}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{inv.name || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{inv.email}</td>
                        <td className="text-center px-4 py-3">
                          {emailSent ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                              <CheckCircle size={14} weight="fill" /> Gönderildi
                            </span>
                          ) : emailFailed ? (
                            <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-medium">
                              <WarningCircle size={14} weight="fill" /> E-posta Başarısız
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium">
                              <PaperPlaneTilt size={14} /> Beklemede
                            </span>
                          )}
                        </td>
                        <td className="text-right px-4 py-3">
                          <div className="inline-flex items-center gap-1">
                            {inv.invite_link && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                                onClick={() => copyLink(inv.invite_link)}
                                title="Davet linkini kopyala"
                                data-testid={`btn-copy-${inv.email}`}
                              >
                                <ShareNetwork size={16} weight="bold" />
                              </Button>
                            )}
                            <Button
                              variant="ghost" size="icon"
                              className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              onClick={() => deleteInvite(inv)}
                              title="Daveti iptal et"
                              data-testid={`btn-del-inv-${inv.email}`}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={(v) => { setInviteOpen(v); if (!v) setCreatedLink(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Kullanıcı Davet Et</DialogTitle>
            <DialogDescription>
              Kullanıcıya davet e-postası gönderilir. Davet kabul edildikten
              sonra rol ve şantiye yetkilerini Roller & İzinler sayfasından
              atayabilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ad Soyad <span className="text-rose-500">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Örn. Ahmet Yılmaz"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label>E-posta <span className="text-rose-500">*</span></Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ornek@sirket.com"
                data-testid="invite-email-input"
              />
            </div>
            {createdLink && (
              <Card className="p-3 border-emerald-200 bg-emerald-50">
                <div className="text-xs font-semibold text-emerald-900 mb-1">
                  Davet linki (e-posta gönderilmediyse manuel paylaşın):
                </div>
                <div className="flex gap-2">
                  <code className="flex-1 text-[10px] p-1.5 bg-white border border-emerald-200 rounded truncate" title={createdLink}>
                    {createdLink}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyLink(createdLink)}>
                    <Copy size={12} className="mr-1" /> Kopyala
                  </Button>
                </div>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviting}>
              Kapat
            </Button>
            <Button
              onClick={invite}
              disabled={inviting}
              className="bg-[#1B4776] hover:bg-[#163a63] text-white"
              data-testid="invite-submit-btn"
            >
              {inviting ? "Gönderiliyor…" : "Davet Et"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
